import React, { useState } from 'react'

import {
  GetVariantSetFramesQuery,
  useGetVariantSetFramesQuery,
} from '@sketch/gql-types'
import {
  TruncateWithTooltip,
  HighlightedText,
  Tooltip,
  useForTablet,
} from '@sketch/components'
import { useAnalytics } from '@sketch/modules-common'
import {
  ListGridItem,
  ItemImage,
  ItemName,
  ItemImageWrapper,
  ItemSecondary,
  ItemTertiary,
} from '../../components/Grid'
import {
  MissingFrameIcon,
  MissingItemName,
  MissingListGridItem,
  VersionLinkStyled,
} from './Symbols.styles'

import { useGetIdentifiers } from '../../../hooks/useGetIdentifiers'
import { INSPECTOR_URL_HASH } from '../../../constants'

import type { Thumbnail, VariantSetParsed } from '../../types'
import { getThumbnail } from '../../utils'

type VariantFrame = NonNullable<
  GetVariantSetFramesQuery['section']
>['frames']['entries'][0]

interface VariantPreview {
  src: string
  srcSet: string
}

export const getVariantPreviews = (
  frames: VariantFrame[]
): VariantPreview[] => {
  return frames
    .map(frame => {
      const thumbnails = {
        '1x': (frame.files.find(f => f.scale === 1)?.thumbnails ??
          []) as Thumbnail[],
        '2x': (frame.files.find(f => f.scale === 2)?.thumbnails ??
          []) as Thumbnail[],
      }

      return {
        src: getThumbnail(thumbnails['1x']),
        srcSet: `
          ${getThumbnail(thumbnails['1x'])},
          ${getThumbnail(thumbnails['2x'])} 2x
        `,
      }
    })
    .filter(preview => preview.src !== '')
}

// Maps a horizontal position within an element to a preview index - e.g. a
// pointer 30% of the way across a 5-preview tile lands on index 1.
export const previewIndexForOffset = (
  offsetX: number,
  width: number,
  previewsCount: number
): number => {
  if (previewsCount < 2 || width <= 0) {
    return 0
  }

  const relativeX = Math.min(1, Math.max(0, offsetX / width))

  return Math.min(previewsCount - 1, Math.floor(relativeX * previewsCount))
}

/**
 * Scrubs through the variant previews as the pointer pans across the item,
 * matching how the Mac app previews variant sets - the leftmost edge shows
 * the first variant, the rightmost edge the last, resetting once the
 * pointer leaves.
 */
export const useVariantPreviewScrub = (previewsCount: number) => {
  const [previewIndex, setPreviewIndex] = useState(0)

  const handlePointerMove = (event: React.MouseEvent<HTMLElement>) => {
    const { left, width } = event.currentTarget.getBoundingClientRect()

    setPreviewIndex(
      previewIndexForOffset(event.clientX - left, width, previewsCount)
    )
  }

  const reset = () => setPreviewIndex(0)

  return { previewIndex, handlePointerMove, reset }
}

const MissingVariantSetGridItem = () => (
  <MissingListGridItem>
    <Tooltip
      content="There was an error processing this variant set and it can't be displayed."
      placement="bottom"
    >
      <ItemImageWrapper>
        <MissingFrameIcon />
      </ItemImageWrapper>
      <MissingItemName>Missing Variant Set</MissingItemName>
    </Tooltip>
  </MissingListGridItem>
)

interface VariantSetItemProps {
  variantSet: VariantSetParsed
  searchValue: string
  groupName?: string
  isSearch?: boolean
}

export const VariantSetItem = ({
  variantSet,
  searchValue,
  groupName,
  isSearch,
}: VariantSetItemProps) => {
  const { shareIdentifier, versionShortId, versionIdentifier } =
    useGetIdentifiers()
  const { trackEvent } = useAnalytics()

  const isTabletAndBigger = useForTablet()
  const isMobile = !isTabletAndBigger

  // The variant frame previews are fetched per variant set: nesting them in
  // the components list query would exceed the API's query complexity limit
  const { data, loading } = useGetVariantSetFramesQuery({
    variables: {
      shareIdentifier,
      sectionUuid: variantSet.uuid,
      versionShortId,
    },
  })

  const frames = data?.section?.frames.entries ?? []
  const previews = getVariantPreviews(frames)

  const { previewIndex, handlePointerMove, reset } = useVariantPreviewScrub(
    previews.length
  )

  if (!loading && previews.length === 0) {
    return <MissingVariantSetGridItem />
  }

  const preview = previews[Math.min(previewIndex, previews.length - 1)]

  const handleMouseEnter = () => {
    // Warm the browser cache so panning across the variants doesn't flicker
    previews.forEach(({ src }) => {
      const image = new window.Image()
      image.src = src
    })
  }

  const totalVariants = data?.section?.frames.meta.totalCount ?? 0

  const item = (
    <ListGridItem>
      <ItemImageWrapper
        onMouseEnter={handleMouseEnter}
        onMouseMove={handlePointerMove}
        onMouseLeave={reset}
      >
        <ItemImage
          alt={variantSet.name}
          src={preview?.src}
          srcSet={preview?.srcSet}
        />
      </ItemImageWrapper>
      <ItemName>
        <TruncateWithTooltip>
          <HighlightedText search={searchValue}>
            {variantSet.name}
          </HighlightedText>
        </TruncateWithTooltip>
      </ItemName>
      {isSearch && (
        <ItemSecondary>
          <TruncateWithTooltip>{groupName}</TruncateWithTooltip>
        </ItemSecondary>
      )}
      {totalVariants > 0 && (
        <ItemTertiary>
          {totalVariants} {totalVariants === 1 ? 'Variant' : 'Variants'}
        </ItemTertiary>
      )}
    </ListGridItem>
  )

  const [firstFrame] = frames

  if (!firstFrame) {
    return item
  }

  return (
    <VersionLinkStyled
      key={`${variantSet.identifier}-${versionIdentifier}`}
      onClick={() => {
        if (searchValue) {
          trackEvent('CWV - search click', { type: 'symbols' })
        }
      }}
      to={{
        // TODO: navigate to the variant set section itself once the canvas
        // supports section permalinks; until then the first variant frame is
        // the closest canvas location
        routeKey: 'FRAME',
        routeParams: {
          shareID: shareIdentifier,
          frameUUID: firstFrame.uuid,
        },
        hash: isMobile ? undefined : INSPECTOR_URL_HASH,
      }}
    >
      {item}
    </VersionLinkStyled>
  )
}
